import { createContext, memo, useCallback, useEffect, useMemo } from 'react';
import { AUDIO } from '../../types/CallMediaTypes';
import { mediaPlaybackActions } from '../stores/AudioPlaybackStore';
import { UNKNOWN_SOURCE } from '../../types/MediaSourceTypes';
import { RecordingHostError } from '../types/ErrorTypes';
import { Metrics } from '../utils/metrics';
import { jsx as _jsx } from "react/jsx-runtime";
export const MediaElementContext = /*#__PURE__*/createContext({
  engagementId: undefined,
  externalAccountId: undefined,
  maybeRequiresAuthUpdate: false,
  isInvalidMediaUrl: null,
  mediaProgressBarRef: {
    current: null
  },
  mediaRef: {
    current: null
  },
  mediaType: AUDIO,
  recordingUrl: '',
  refetchedMediaUrl: false,
  source: UNKNOWN_SOURCE,
  setCurrentTime: () => undefined,
  setDuration: () => undefined,
  setRefetchedMediaUrl: () => undefined,
  setStatus: () => undefined,
  setErrorStatus: () => undefined,
  setMaybeRequiresAuthUpdate: () => undefined
});
const defaultRef = {
  current: null
};
const HTTPS_REGEX = /^https:\/\//;
const MediaElementProvider = /*#__PURE__*/memo(({
  children,
  engagementId,
  externalAccountId,
  maybeRequiresAuthUpdate = false,
  mediaProgressBarRef,
  mediaRef,
  mediaStateDispatch,
  mediaType = AUDIO,
  recordingUrl,
  source,
  refetchedMediaUrl
}) => {
  const setCurrentTime = useCallback(time => {
    mediaStateDispatch(mediaPlaybackActions.setCurrentTime(time));
  }, [mediaStateDispatch]);
  const setDuration = useCallback(duration => {
    mediaStateDispatch(mediaPlaybackActions.loadedMetadata(duration));
  }, [mediaStateDispatch]);
  const setPercentLoaded = useCallback(percentLoaded => {
    mediaStateDispatch(mediaPlaybackActions.setPercentLoaded(percentLoaded));
  }, [mediaStateDispatch]);
  const setStatus = useCallback(status => {
    mediaStateDispatch(mediaPlaybackActions.setStatus(status));
  }, [mediaStateDispatch]);
  const setErrorStatus = useCallback(errorType => {
    mediaStateDispatch(mediaPlaybackActions.setErrorStatus(errorType));
  }, [mediaStateDispatch]);
  const setRefetchedMediaUrl = useCallback(state => {
    mediaStateDispatch(mediaPlaybackActions.setRefetchedMediaUrl(state));
  }, [mediaStateDispatch]);
  const setMaybeRequiresAuthUpdate = useCallback(state => {
    mediaStateDispatch(mediaPlaybackActions.setMaybeRequiresAuthUpdate(state));
  }, [mediaStateDispatch]);
  const isInvalidMediaUrl = useMemo(() => {
    if (!recordingUrl) {
      return RecordingHostError.NO_URL;
    } else if (!HTTPS_REGEX.test(recordingUrl)) {
      return RecordingHostError.INSECURE_HOST;
    }
    return null;
  }, [recordingUrl]);
  useEffect(() => {
    if (isInvalidMediaUrl) {
      setErrorStatus(isInvalidMediaUrl);
      Metrics.counter('insecure-media-url', {
        source: source || 'unknown'
      }).increment();
    }
  }, [isInvalidMediaUrl, setErrorStatus, source]);
  const value = useMemo(() => ({
    engagementId: engagementId ? `${engagementId}` : undefined,
    externalAccountId: externalAccountId || undefined,
    isInvalidMediaUrl,
    maybeRequiresAuthUpdate,
    mediaProgressBarRef: mediaProgressBarRef || defaultRef,
    mediaRef,
    mediaType,
    recordingUrl,
    refetchedMediaUrl,
    setCurrentTime,
    setDuration,
    setErrorStatus,
    setMaybeRequiresAuthUpdate,
    setPercentLoaded,
    setRefetchedMediaUrl,
    setStatus,
    source
  }), [engagementId, externalAccountId, isInvalidMediaUrl, maybeRequiresAuthUpdate, mediaProgressBarRef, mediaRef, mediaType, recordingUrl, refetchedMediaUrl, setCurrentTime, setDuration, setErrorStatus, setMaybeRequiresAuthUpdate, setPercentLoaded, setRefetchedMediaUrl, setStatus, source]);
  return /*#__PURE__*/_jsx(MediaElementContext.Provider, {
    value: value,
    children: children
  });
});
export { MediaElementProvider };